import React, { useEffect, useState } from 'react';
import SearchableDropdown from '../components/SearchableDropdown';
import RackCard from '../components/RackCard';
import Filters from '../components/Filters';

// Импортируем данные
import carsData from '../data/cars.json';
import racksData from '../data/racks.json';
import adaptersData from '../data/adapters.json';

// Параметры компонента SelectorScreen
const SelectorScreen = ({
  darkMode,
  selectedBrand,
  setSelectedBrand,
  selectedModel,
  setSelectedModel,
  selectedModification,
  setSelectedModification,
  selectedRoofType,
  setSelectedRoofType,
  showResults,
  setShowResults,
  handleSearch,
  // Массив всех багажников
  racks = [],
  // Отфильтрованные багажники
  filteredRacks = [],
  // Функция для установки отфильтрованных багажников
  setFilteredRacks = () => { },
  // Доступные багажники
  availableRacks = [],
  setAvailableRacks = () => { },
  showFilters,
  setShowFilters,
  filters = {},
  setFilters = () => { },
  resetFilters = () => { },
  activeDropdown,
  setActiveDropdown,
  searchTerm,
  setSearchTerm,
  dropdownRef,
  currentSlide = {},
  handleNextSlide = () => { },
  handlePrevSlide = () => { },
  addToCart = () => { },
  isInCart = () => false,
  toggleFavorite = () => { },
  isFavorite = () => false
}) => {
  // Состояние для хранения отфильтрованных данных автомобилей
  const [filteredCars, setFilteredCars] = useState([]);

  // Ensure carsData is an array before using it
  const safeCarData = Array.isArray(carsData) ? carsData : [];

  // Initialize filteredCars with safe data
  useEffect(() => {
    setFilteredCars(safeCarData);
  }, []);

  // При загрузке компонента, если есть выбранная марка и модель,
  // нужно обновить filteredCars
  useEffect(() => {
    if (selectedBrand && Array.isArray(safeCarData)) {
      const filteredByBrand = safeCarData.filter(car => car.brand === selectedBrand);
      setFilteredCars(filteredByBrand);
    }
  }, []);

  // Фильтрация моделей при выборе бренда
  useEffect(() => {
    if (selectedBrand && Array.isArray(safeCarData)) {
      setFilteredCars(safeCarData.filter(car => car.brand === selectedBrand));
    } else {
      setFilteredCars(safeCarData);
    }
  }, [selectedBrand, safeCarData]);

  // Применение фильтров при их изменении
  useEffect(() => {
    // Ensure we have all required data before proceeding
    if (!showResults || !Array.isArray(availableRacks) || availableRacks.length === 0) return;

    // Ensure filters object has all required properties
    const safeFilters = {
      brands: Array.isArray(filters.brands) ? filters.brands : [],
      colors: Array.isArray(filters.colors) ? filters.colors : [],
      barTypes: Array.isArray(filters.barTypes) ? filters.barTypes : [],
      countries: Array.isArray(filters.countries) ? filters.countries : [],
      capacities: Array.isArray(filters.capacities) ? filters.capacities : [],
      lock: filters.lock,
      priceRanges: Array.isArray(filters.priceRanges) ? filters.priceRanges : []
    };

    let filtered = [...availableRacks];

    // Фильтрация по бренду
    if (safeFilters.brands.length > 0) {
      filtered = filtered.filter(rack => safeFilters.brands.includes(rack.brand));
    }

    // Фильтрация по цвету
    if (safeFilters.colors.length > 0) {
      filtered = filtered.filter(rack => safeFilters.colors.includes(rack.color));
    }

    // Фильтрация по типу дуги
    if (safeFilters.barTypes.length > 0) {
      filtered = filtered.filter(rack => safeFilters.barTypes.includes(rack.bar_type));
    }

    // Фильтрация по стране
    if (safeFilters.countries.length > 0) {
      filtered = filtered.filter(rack => safeFilters.countries.includes(rack.country));
    }

    // Фильтрация по грузоподъемности
    if (safeFilters.capacities.length > 0) {
      filtered = filtered.filter(rack => safeFilters.capacities.includes(rack.capacity));
    }

    // Фильтрация по наличию замка
    if (safeFilters.lock) {
      filtered = filtered.filter(rack => rack.lock === safeFilters.lock);
    }

    // Фильтрация по ценовым диапазонам
    if (safeFilters.priceRanges && safeFilters.priceRanges.length > 0) {
      filtered = filtered.filter(rack => {
        const totalPrice = rack.price + (rack.needsAdapter ? rack.adapterPrice : 0);
        return safeFilters.priceRanges.some(range => {
          if (range.min && range.max) {
            return totalPrice >= range.min && totalPrice <= range.max;
          } else if (range.min) {
            return totalPrice >= range.min;
          } else if (range.max) {
            return totalPrice <= range.max;
          }
          return false;
        });
      });
    } else if (safeFilters.priceRange) {
      // Поддержка старого формата фильтра цены
      filtered = filtered.filter(rack => {
        const totalPrice = rack.price + (rack.needsAdapter ? rack.adapterPrice : 0);
        const range = safeFilters.priceRange;

        if (range.min && range.max) {
          return totalPrice >= range.min && totalPrice <= range.max;
        } else if (range.min) {
          return totalPrice >= range.min;
        } else if (range.max) {
          return totalPrice <= range.max;
        }
        return true;
      });
    }

    setFilteredRacks(filtered);
  }, [filters, availableRacks, showResults, setFilteredRacks]);

  // Получение доступных багажников для выбранного автомобиля
  const getAvailableRacks = () => {
    // Находим все автомобили, соответствующие выбранным параметрам
    const selectedCars = carsData.filter(car =>
      car.brand === selectedBrand &&
      car.model === selectedModel &&
      car.modification === selectedModification &&
      car.roof_type === selectedRoofType
    );

    if (selectedCars.length === 0) return [];

    // Создаем массив для хранения всех подходящих багажников
    let availableRacks = [];

    // Для каждого найденного автомобиля находим соответствующий багажник
    selectedCars.forEach(selectedCar => {
      // Находим багажник по ID
      const rack = racksData.find(rack => rack.id === selectedCar.rack_id);

      if (rack) {
        // Если нужен адаптер, добавляем информацию о нем
        if (selectedCar.adapter) {
          // Преобразуем ID адаптера в строку для корректного сравнения
          const adapterId = String(selectedCar.adapter);
          const adapter = adaptersData.find(a => String(a.id) === adapterId);

          availableRacks.push({
            ...rack,
            needsAdapter: true,
            adapterInfo: adapter,
            adapterPrice: adapter ? adapter.price : 0,
            carId: selectedCar.id // Добавляем ID автомобиля для уникальности
          });
        } else {
          availableRacks.push({
            ...rack,
            needsAdapter: false,
            adapterInfo: null,
            adapterPrice: 0,
            carId: selectedCar.id // Добавляем ID автомобиля для уникальности
          });
        }
      }
    });

    return availableRacks;
  };

  // Обработчик поиска багажников
  const handleSearchRacks = () => {
    // Сбрасываем фильтры при новом поиске
    resetFilters();

    const availableRacksData = getAvailableRacks();
    setAvailableRacks(availableRacksData);
    setFilteredRacks(availableRacksData);
    setShowResults(true);
  };

  // Обработчик переключения фильтров
  const handleToggleFilter = (type, value) => {
    setFilters(prev => {
      const newFilters = { ...prev };

      switch (type) {
        case 'brand':
          if (newFilters.brands.includes(value)) {
            newFilters.brands = newFilters.brands.filter(b => b !== value);
          } else {
            newFilters.brands = [...newFilters.brands, value];
          }
          break;
        case 'color':
          if (newFilters.colors.includes(value)) {
            newFilters.colors = newFilters.colors.filter(c => c !== value);
          } else {
            newFilters.colors = [...newFilters.colors, value];
          }
          break;
        case 'barType':
          if (newFilters.barTypes.includes(value)) {
            newFilters.barTypes = newFilters.barTypes.filter(t => t !== value);
          } else {
            newFilters.barTypes = [...newFilters.barTypes, value];
          }
          break;
        case 'country':
          if (newFilters.countries.includes(value)) {
            newFilters.countries = newFilters.countries.filter(c => c !== value);
          } else {
            newFilters.countries = [...newFilters.countries, value];
          }
          break;
        case 'capacity':
          if (newFilters.capacities.includes(value)) {
            newFilters.capacities = newFilters.capacities.filter(c => c !== value);
          } else {
            newFilters.capacities = [...newFilters.capacities, value];
          }
          break;
        case 'lock':
          newFilters.lock = newFilters.lock === value ? null : value;
          break;
        case 'priceRange':
          // Если передан null, сбрасываем фильтр цены
          if (value === null) {
            newFilters.priceRange = null;
          }
          // Если текущий диапазон совпадает с новым, сбрасываем его
          else if (newFilters.priceRange &&
            ((newFilters.priceRange.min === value.min && newFilters.priceRange.max === value.max) ||
              (newFilters.priceRange.min === value.min && !newFilters.priceRange.max && !value.max) ||
              (!newFilters.priceRange.min && !value.min && newFilters.priceRange.max === value.max))) {
            newFilters.priceRange = null;
          }
          // Иначе устанавливаем новый диапазон
          else {
            newFilters.priceRange = value;
          }
          break;
        default:
          break;
      }

      return newFilters;
    });
  };

  return (
    <>
      {!showResults ? (
        <>
          <div className={`rounded-lg shadow p-4 mb-6 ${darkMode ? 'bg-gray-800 text-white' : 'bg-white'
            }`}>
            <SearchableDropdown
              id="brand"
              title="Марка автомобиля"
              options={safeCarData} // Используем полный список автомобилей
              selected={selectedBrand}
              onSelect={(brand) => {
                // Добавим обработчик, который будет сбрасывать все зависимые поля
                setSelectedBrand(brand);
                setSelectedModel('');
                setSelectedModification('');
                setSelectedRoofType(''); // Важно: сбрасываем тип крыши
              }}
              searchable={true}
              darkMode={darkMode}
              activeDropdown={activeDropdown}
              setActiveDropdown={setActiveDropdown}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              dropdownRef={dropdownRef}
            />

            {selectedBrand && (
              <SearchableDropdown
                id="model"
                title="Модель"
                options={filteredCars.filter(car => car.brand === selectedBrand)}
                selected={selectedModel}
                onSelect={setSelectedModel}
                searchable={true}
                darkMode={darkMode}
                activeDropdown={activeDropdown}
                setActiveDropdown={setActiveDropdown}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                dropdownRef={dropdownRef}
              />
            )}

            {selectedModel && (
              <SearchableDropdown
                id="modification"
                title="Модификация"
                options={filteredCars.filter(car =>
                  car.brand === selectedBrand &&
                  car.model === selectedModel
                )}
                selected={selectedModification}
                onSelect={setSelectedModification}
                searchable={false}
                darkMode={darkMode}
                activeDropdown={activeDropdown}
                setActiveDropdown={setActiveDropdown}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                dropdownRef={dropdownRef}
              />
            )}

            {selectedModification && (
              <SearchableDropdown
                id="roofType"
                title="Тип крыши"
                options={filteredCars.filter(car =>
                  car.brand === selectedBrand &&
                  car.model === selectedModel &&
                  car.modification === selectedModification
                )}
                selected={selectedRoofType}
                onSelect={setSelectedRoofType}
                searchable={false}
                darkMode={darkMode}
                activeDropdown={activeDropdown}
                setActiveDropdown={setActiveDropdown}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                dropdownRef={dropdownRef}
              />
            )}
          </div>

          <button
            onClick={handleSearchRacks}
            className="w-full py-3 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!selectedBrand || !selectedModel || !selectedModification || !selectedRoofType}
          >
            Найти подходящие багажники
          </button>
        </>
      ) : (
        <>
          <div className={`rounded-lg shadow p-4 mb-4 ${darkMode ? 'bg-gray-800 text-white' : 'bg-white'
            }`}>
            <h3 className="font-medium mb-2">Выбранный автомобиль:</h3>
            {/*<p><strong>Марка:</strong> {selectedBrand}</p>
            <p><strong>Модель:</strong> {selectedModel}</p>
            <p><strong>Модификация:</strong> {selectedModification}</p>
            <p><strong>Тип крыши:</strong> {selectedRoofType}</p>*/}
            <p><strong>{selectedBrand} {selectedModel} {selectedModification} {selectedRoofType}</strong></p>

            <button
              onClick={() => setShowResults(false)}
              className={`w-full py-3 px-4 rounded-lg mt-4 ${darkMode
                ? 'bg-gray-700 text-white hover:bg-gray-600'
                : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                }`}
            >
              Изменить параметры
            </button>
          </div>



          <div className="flex justify-between items-center mb-4">
            <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : ''
              }`}>Подходящие багажники</h3>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center px-3 py-2 rounded-lg ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-800'
                }`}
            >
              <i className="fas fa-filter mr-2"></i>
              Фильтры
            </button>
          </div>

          {showFilters && (
            <Filters
              darkMode={darkMode}
              filters={filters}
              toggleFilter={handleToggleFilter}
              resetFilters={resetFilters}
              racks={availableRacks}
            />
          )}

          {filteredRacks.length > 0 ? (
            filteredRacks.map(rack => (
              <RackCard
                key={`${rack.id}-${rack.carId || 0}`}
                rack={rack}
                darkMode={darkMode}
                currentSlide={currentSlide}
                handleNextSlide={handleNextSlide}
                handlePrevSlide={handlePrevSlide}
                addToCart={addToCart}
                isInCart={(rackId) => isInCart(rackId, rack.carId)}
                toggleFavorite={toggleFavorite}
                isFavorite={isFavorite}
                selectedBrand={selectedBrand}
                selectedModel={selectedModel}
                selectedModification={selectedModification}
                selectedRoofType={selectedRoofType}
              />
            ))
          ) : (
            <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <i className="fas fa-search text-4xl mb-3"></i>
              <p>Нет багажников, соответствующих выбранным фильтрам</p>
              <button
                onClick={resetFilters}
                className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Сбросить фильтры
              </button>
            </div>
          )}

          <button
            onClick={() => setShowResults(false)}
            className={`w-full py-3 px-4 rounded-lg mt-4 ${darkMode
              ? 'bg-gray-700 text-white hover:bg-gray-600'
              : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
          >
            Изменить параметры
          </button>
        </>
      )}
    </>
  );
};

export default SelectorScreen;
